/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Platform} from '@app/lib/Platform';
import UpdaterStore from '@app/stores/UpdaterStore';
import {Trans} from '@lingui/react/macro';
import {ArrowsClockwiseIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

// The native (Squirrel) check resolves asynchronously via updater events, so a manual
// check must keep showing "Checking…" for a grace window rather than instantly claiming
// "Up to date" the moment checkForUpdates() returns.
const CHECK_GRACE_MS = 12000;

export const UpdateChecker = observer(() => {
	const store = UpdaterStore;
	const ready = store.nativeUpdateReady;
	const downloading = store.nativeUpdatePending;
	const [checking, setChecking] = useState(false);

	useEffect(() => {
		if (!checking) {
			return;
		}
		if (ready || downloading) {
			setChecking(false);
			return;
		}
		const timer = setTimeout(() => setChecking(false), CHECK_GRACE_MS);
		return () => clearTimeout(timer);
	}, [checking, ready, downloading]);

	if (!Platform.isElectron) {
		return null;
	}

	const handleClick = () => {
		if (ready) {
			void store.applyUpdate();
			return;
		}
		if (checking || downloading) {
			return;
		}
		setChecking(true);
		void store.checkForUpdates(true);
	};

	const isChecking = checking || store.isChecking;
	const busy = isChecking || downloading;

	const label = (() => {
		if (ready) {
			return <Trans>Restart to update</Trans>;
		}
		if (downloading) {
			return <Trans>Downloading update...</Trans>;
		}
		if (isChecking) {
			return <Trans>Checking for updates...</Trans>;
		}
		return <Trans>Check for updates</Trans>;
	})();

	const showUpToDate = !busy && !ready && store.lastCheckedAt != null && !store.hasUpdate;

	return (
		<div style={{marginTop: '0.5rem'}}>
			<button
				type="button"
				onClick={handleClick}
				disabled={busy}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: '0.375rem',
					padding: 0,
					border: 'none',
					background: 'none',
					font: 'inherit',
					fontSize: '0.6875rem',
					lineHeight: 1.25,
					color: 'var(--text-link)',
					cursor: busy ? 'default' : 'pointer',
					opacity: busy ? 0.7 : 1,
				}}
			>
				<ArrowsClockwiseIcon weight="bold" style={{width: '0.875rem', height: '0.875rem'}} />
				{label}
			</button>
			{showUpToDate && (
				<div style={{fontSize: '0.625rem', lineHeight: 1.25, color: 'var(--text-muted)', marginTop: '0.125rem'}}>
					<Trans>Up to date</Trans>
				</div>
			)}
		</div>
	);
});
